import {
  ChainNotConfiguredError,
  ProviderNotFoundError,
  SwitchChainError,
  UserRejectedRequestError,
  WalletAccountError,
  createConnector,
  type Connector,
  type Evaluate,
  type Prettify,
  type RpcError,
  type SolanaProvider,
} from '@particle-network/connector-core';
import { PublicKey } from '@solana/web3.js';
import { isConnected } from './utils';

type Target = {
  icon?: string;
  id: string;
  name: string;
  provider: (window?: Window | undefined) => SolanaProvider | undefined;
};

type TargetId = 'coinbaseWallet' | 'bitKeep' | 'trustWallet' | 'okxWallet' | 'phantom' | 'braveWallet';

type TargetMap = { [_ in TargetId]: Target };

export type InjectedParameters = {
  unstableShimAsyncInject?: boolean | number | undefined;
  target: TargetId | Target | (() => Target);
};

type Window = {
  coinbaseSolana?: any | undefined;
  bitkeep?: any | undefined;
  phantom?: any | undefined;
  trustwallet?: any | undefined;
  okxwallet?: any | undefined;
  braveSolana?: any | undefined;
};

const targetMap = {
  coinbaseWallet: {
    id: 'coinbaseWallet',
    name: 'Coinbase Wallet',
    provider(window) {
      if (typeof window !== 'undefined') {
        return window?.coinbaseSolana;
      }
      return undefined;
    },
  },
  bitKeep: {
    id: 'bitKeep',
    name: 'Bitget',
    provider(window) {
      if (typeof window !== 'undefined' && window.bitkeep?.solana?.isBitKeep) {
        return window.bitkeep?.solana;
      }
    },
  },
  phantom: {
    id: 'phantom',
    name: 'Phantom',
    provider(window) {
      if (typeof window !== 'undefined' && window.phantom?.solana?.isPhantom) {
        return window.phantom?.solana;
      }
    },
  },
  trustWallet: {
    id: 'trustWallet',
    name: 'Trust Wallet',
    provider(window) {
      if (typeof window !== 'undefined' && window.trustwallet?.solana?.isTrust) {
        return window.trustwallet?.solana;
      }
    },
  },
  okxWallet: {
    id: 'okxWallet',
    name: 'OKX Wallet',
    provider(window) {
      if (typeof window !== 'undefined' && window.okxwallet?.solana?.isOkxWallet) {
        return window.okxwallet?.solana;
      }
    },
  },
  braveWallet: {
    id: 'braveWallet',
    name: 'Brave Wallet',
    provider(window) {
      if (typeof window !== 'undefined' && window.braveSolana?.isBraveWallet) {
        return window.braveSolana;
      }
    },
  },
} as const satisfies TargetMap;

injected.type = 'injected' as const;
export function injected(parameters: InjectedParameters) {
  const { unstableShimAsyncInject } = parameters;

  function getTarget(): Evaluate<Target & { id: string }> {
    const target = parameters.target;
    if (typeof target === 'function') {
      const result = target();
      if (result) return result;
    }

    if (typeof target === 'object') return target;

    if (typeof target === 'string' && targetMap[target as keyof typeof targetMap])
      return targetMap[target as keyof typeof targetMap];

    throw new Error('target not found.');
  }

  type Provider = SolanaProvider | undefined;
  type Properties = {
    publicKey?: PublicKey;
    onAccountChanged(publicKey: PublicKey): void;
  };
  type StorageItem = Prettify<
    {
      [_ in `${string}.injected.connected`]: true;
    } & {
      [_ in `${string}.injected.chainId`]: number;
    }
  >;

  let accountChanged: Properties['onAccountChanged'] | undefined;
  let disconnect: Connector['onDisconnect'] | undefined;

  return createConnector<Provider, Properties, StorageItem>((config) => ({
    get icon() {
      return getTarget().icon;
    },
    get id() {
      return `solana_${getTarget().id}`;
    },
    get name() {
      return getTarget().name;
    },
    type: injected.type,
    async connect({ chainId, isReconnecting } = {}) {
      const provider = await this.getProvider();
      if (!provider) throw new ProviderNotFoundError();

      let accounts: readonly string[] = [];
      if (isReconnecting) {
        const connected = await config.storage?.getItem(`${this.id}.injected.connected`);
        if (connected) {
          if (!isConnected(provider)) {
            await provider.connect();
          }
          if (!provider.publicKey) throw new WalletAccountError(this.id);
        }
        accounts = await this.getAccounts().catch(() => []);
      }

      try {
        if (!accounts?.length && !isReconnecting) {
          if (!isConnected(provider)) {
            await provider.connect();
          }

          if (!provider.publicKey) throw new WalletAccountError(this.id);
          accounts = [provider.publicKey.toBase58()];
        }

        if (!accountChanged) {
          accountChanged = this.onAccountChanged.bind(this);
          provider.on('accountChanged', accountChanged);
        }
        if (!disconnect) {
          disconnect = this.onDisconnect.bind(this);
          provider.on('disconnect', disconnect);
        }

        // Switch to chain if provided
        let currentChainId = await this.getChainId();
        if (chainId && currentChainId !== chainId) {
          const chain = await this.switchChain!({ chainId }).catch((error) => {
            if (error.code === UserRejectedRequestError.code) throw error;
            return { id: currentChainId };
          });
          currentChainId = chain?.id ?? currentChainId;
        }

        // Add connected shim
        await config.storage?.setItem(`${this.id}.injected.connected`, true);

        if (provider.publicKey) {
          this.publicKey = new PublicKey(provider.publicKey.toBytes());
        }

        return { accounts, chainId: currentChainId };
      } catch (err) {
        const error = err as RpcError;
        if (error.code === UserRejectedRequestError.code) throw new UserRejectedRequestError(error);
        throw error;
      }
    },
    async disconnect() {
      const provider = await this.getProvider();
      if (!provider) throw new ProviderNotFoundError();

      if (disconnect) {
        provider.removeListener('disconnect', disconnect);
        disconnect = undefined;
      }

      try {
        await provider.disconnect();
      } catch {
        // ignore
      }

      await config.storage?.removeItem(`${this.id}.injected.connected`);
      this.publicKey = undefined;
    },
    async getAccounts() {
      const provider = await this.getProvider();
      if (!provider) throw new ProviderNotFoundError();
      if (this.publicKey) {
        return [this.publicKey.toBase58()];
      }

      const connected = await config.storage?.getItem(`${this.id}.injected.connected`);
      if (connected) {
        if (!isConnected(provider)) {
          await provider.connect();
        }
        if (provider.publicKey) {
          this.publicKey = new PublicKey(provider.publicKey.toBytes());
          return [this.publicKey.toBase58()];
        }
      }

      return [];
    },
    async getChainId() {
      const provider = await this.getProvider();
      if (!provider) throw new ProviderNotFoundError();

      const chainId = await config.storage?.getItem(`${this.id}.injected.chainId`);
      if (chainId && config.chains.find((chain) => chain.id === Number(chainId))) {
        return Number(chainId);
      }
      return config.chains[0].id;
    },
    async getProvider() {
      if (typeof window === 'undefined') return undefined;

      const target = getTarget();
      const provider: Provider = target.provider(window as Window);
      return provider;
    },
    async isAuthorized() {
      try {
        let provider = await this.getProvider();
        if (!provider) {
          if (unstableShimAsyncInject !== undefined && unstableShimAsyncInject !== false) {
            const handleProvider = async () => {
              const provider = await this.getProvider();
              return provider;
            };
            const timeout = typeof unstableShimAsyncInject === 'number' ? unstableShimAsyncInject : 1_000;
            provider = await new Promise<Provider>((resolve) => setTimeout(() => resolve(handleProvider()), timeout));
            if (!provider) {
              throw new ProviderNotFoundError();
            }
          } else {
            throw new ProviderNotFoundError();
          }
        }

        const connected = await config.storage?.getItem(`${this.id}.injected.connected`);
        return Boolean(connected);
      } catch {
        return false;
      }
    },
    async switchChain({ chainId }) {
      const chain = config.chains.find((chain) => chain.id === chainId);
      if (!chain) {
        throw new SwitchChainError(new ChainNotConfiguredError());
      }
      await config.storage?.setItem(`${this.id}.injected.chainId`, chainId);
      config.emitter.emit('change', { chainId });
      return chain;
    },
    async onAccountChanged(publicKey) {
      if (!publicKey) {
        this.publicKey = undefined;
        // Disconnect if there are no accounts
        this.onDisconnect();
      } else {
        this.publicKey = new PublicKey(publicKey.toBytes());
        config.emitter.emit('change', {
          accounts: [this.publicKey.toBase58()],
        });
      }
    },
    onChainChanged(chain) {
      const chainId = Number(chain);
      config.emitter.emit('change', { chainId });
    },
    async onDisconnect() {
      const provider = await this.getProvider();
      config.emitter.emit('disconnect');
      if (provider) {
        if (disconnect) {
          provider.removeListener('disconnect', disconnect);
          disconnect = undefined;
        }
      }
      this.publicKey = undefined;
      await config.storage?.removeItem(`${this.id}.injected.connected`);
    },
    isInstalled(): boolean {
      if (typeof window === 'undefined') return false;

      const target = getTarget();
      const provider = target.provider(window as Window);
      return Boolean(provider);
    },
  }));
}
